import React, { createContext, useContext, useState, useEffect } from "react";
import { auth, db } from "../lib/firebase";
import { onAuthStateChanged, signOut as firebaseSignOut } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // ANTI-TELA BRANCA: Se o Firebase demorar mais de 10s para responder,
    // forçamos o encerramento do loading para que o sistema de rotas possa agir.
    const timer = setTimeout(() => {
      setLoading((prevLoading) => {
        if (prevLoading) {
          console.warn("Auth Timeout: Finalizando loading para evitar tela branca.");
          return false;
        }
        return prevLoading;
      });
    }, 10000);

    let unsubscribeSnapshot = null;

    const unsubscribeAuth = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);

        const userRef = doc(db, "users", firebaseUser.uid);
        
        if (unsubscribeSnapshot) unsubscribeSnapshot();

        unsubscribeSnapshot = onSnapshot(userRef, (userDoc) => {
          if (userDoc.exists()) {
            const data = userDoc.data();

            // ANTIBAN EM TEMPO REAL: Se o status mudar para banned no banco, chuta na hora!
            if (data.status === 'banned') {
              console.warn("Usuário banido detectado. Encerrando sessão.");
              firebaseSignOut(auth); // Desloga do Firebase à força
              setUser(null);
              setUserProfile(null);
              setLoading(false);
              return;
            }
            
            // CONFERE SE A DATA DE EXPIRAÇÃO DA ASSINATURA JÁ VENCEU
            let currentPaymentStatus = data.paymentStatus;
            
            if (data.subscriptionExpiresAt) {
              const expireDate = new Date(data.subscriptionExpiresAt);
              const today = new Date();
              
              if (today > expireDate) {
                currentPaymentStatus = 'expired';
              }
            }

            // Define o perfil atualizado na memória do React
            setUserProfile({
              ...data,
              uid: firebaseUser.uid,
              paymentStatus: currentPaymentStatus,
              isNewUser: false // Conta confirmada e preenchida no Firestore
            });
          } else {
            // CORREÇÃO CRUCIAL PARA O GOOGLE: Se a conta acabou de ser criada, o doc
            // pode não existir no primeiro milissegundo. Não limpamos o usuário!
            // Marcamos apenas como um novo usuário para a tela gerenciar o salvamento.
            setUserProfile({ isNewUser: true });
          }
          setLoading(false);
        }, (error) => {
          console.error("Erro no snapshot do Firestore:", error);
          setLoading(false);
        });

      } else {
        // Se deslogou voluntariamente, limpa tudo de verdade
        if (unsubscribeSnapshot) {
          unsubscribeSnapshot();
          unsubscribeSnapshot = null;
        }
        setUser(null);
        setUserProfile(null);
        setLoading(false);
      }
      
      clearTimeout(timer);
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeSnapshot) unsubscribeSnapshot();
      clearTimeout(timer);
    };
  }, []);

  const signOut = async () => {
    try {
      setLoading(true);
      await firebaseSignOut(auth);
      setUser(null);
      setUserProfile(null);
    } catch (error) {
      console.error("Erro ao deslogar:", error);
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    signOut,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading ? children : (
        <div className="min-h-screen flex items-center justify-center bg-white">
          <div className="flex flex-col items-center gap-4">
             <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600"></div>
             <p className="font-black italic uppercase text-[10px] text-slate-400 tracking-widest">Sincronizando Acesso...</p>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export default AuthContext;